import clsx from 'clsx';
import type { MouseEvent, ReactNode } from 'react';
import type { ButtonAriaProps } from './Btn';

export interface BtnIconOnlyProps {
    icon: ReactNode;
    /** Accessible name. Also used as the hover tooltip unless `buttonTooltip` is set. */
    ariaLabel: string;
    /** Square hit target. `md` is the default (52px). */
    size?: 'sm' | 'md' | 'xl';
    variant?: 'default' | 'danger' | 'warning';
    /**
     * `transparent` (default) shows the surface behind the control.
     * `opaque` is the navy-header chip: white background, navy icons. Required on any blue card header.
     * Combined with `variant="danger"`, the icon stays navy at rest and fills red on hover / press / tap.
     */
    fill?: 'transparent' | 'opaque';
    disabled?: boolean;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    buttonType?: 'button' | 'submit' | 'reset';
    buttonClasses?: string;
    buttonAriaProps?: ButtonAriaProps;
    buttonTooltip?: string;
}

const BtnIconOnly = ({
    icon,
    ariaLabel,
    size = 'md',
    variant = 'default',
    fill = 'transparent',
    disabled = false,
    onClick,
    buttonType = 'button',
    buttonClasses,
    buttonAriaProps,
    buttonTooltip,
}: BtnIconOnlyProps) => (
    <button
        type={buttonType}
        className={clsx(
            'btn',
            'btn-icon-only',
            size === 'sm' && 'btn-icon-only-sm',
            size === 'xl' && 'btn-icon-only-xl',
            variant === 'danger' && 'btn-icon-only-danger',
            variant === 'warning' && 'btn-icon-only-warning',
            fill === 'opaque' && 'btn-icon-only-opaque',
            buttonClasses,
        )}
        aria-label={ariaLabel}
        title={buttonTooltip ?? ariaLabel}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        {...buttonAriaProps}
    >
        <span className="btn-icon-wrap" aria-hidden="true">
            {icon}
        </span>
    </button>
);

export default BtnIconOnly;
